import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { reportQueryError } from "@/lib/sentry";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { trpc, trpcClient } from "@/lib/trpc";
import { AuthProvider } from "@/contexts/AuthContext";
import { RequireRole } from "@/components/layout/RequireRole";
import { UpdateBanner } from "@/components/UpdateBanner";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tenants from "./pages/Tenants";
import Revenue from "./pages/Revenue";
import PlatformHealth from "./pages/PlatformHealth";
import AiUsage from "./pages/AiUsage";
import Rgpd from "./pages/Rgpd";
import TenantDetail from "./pages/TenantDetail";
import StaffManagement from "./pages/StaffManagement";
import InternalAccounts from "./pages/InternalAccounts";
import Freelances from "./pages/Freelances";
import Commissions from "./pages/Commissions";
import CommissionPlan from "./pages/CommissionPlan";
import AuditTrail from "./pages/AuditTrail";
import SystemSettings from "./pages/SystemSettings";
import TravauxBTPSupervision from "./pages/TravauxBTPSupervision";
import BroadcastCenter from "./pages/BroadcastCenter";
import CohortsAnalysis from "./pages/CohortsAnalysis";
import TenantHealth from "./pages/TenantHealth";
import FeatureFlags from "./pages/FeatureFlags";
import Referrals from "./pages/Referrals";
import ReviewModeration from "./pages/ReviewModeration";
import AccessManagement from "./pages/AccessManagement";
import Categories from "./pages/Categories";
import ApiKeys from "./pages/ApiKeys";
import Pricing from "./pages/Pricing";
import SystemAnalytics from "./pages/SystemAnalytics";
import AiCosts from "./pages/AiCosts";

const queryClient = new QueryClient({
  // Central Sentry capture for every query/mutation failure (skips expected
  // control-flow HTTP errors). Replaces the need to instrument each catch.
  queryCache: new QueryCache({
    onError: (error, query) => reportQueryError(error, { queryKey: query.queryKey }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportQueryError(error, { mutationKey: mutation.options.mutationKey }),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireRole>
            <Dashboard />
          </RequireRole>
        }
      />
      <Route
        path="/tenants"
        element={
          <RequireRole>
            <Tenants />
          </RequireRole>
        }
      />
      <Route
        path="/tenants/:id"
        element={
          <RequireRole>
            <TenantDetail />
          </RequireRole>
        }
      />
      <Route
        path="/revenue"
        element={
          <RequireRole>
            <Revenue />
          </RequireRole>
        }
      />
      <Route
        path="/platform-health"
        element={
          <RequireRole>
            <PlatformHealth />
          </RequireRole>
        }
      />
      <Route
        path="/ai-usage"
        element={
          <RequireRole>
            <AiUsage />
          </RequireRole>
        }
      />
      <Route
        path="/rgpd"
        element={
          <RequireRole>
            <Rgpd />
          </RequireRole>
        }
      />
      <Route
        path="/staff"
        element={
          <RequireRole>
            <StaffManagement />
          </RequireRole>
        }
      />
      <Route
        path="/internal-accounts"
        element={
          <RequireRole>
            <InternalAccounts />
          </RequireRole>
        }
      />
      {/* Lot A — freelances de prospection (SDR / Sales), invitation e-mail. */}
      <Route
        path="/freelances"
        element={
          <RequireRole>
            <Freelances />
          </RequireRole>
        }
      />
      {/* Lot B — commissions freelances : paiement, export CSV, barème (SUPER_ADMIN). */}
      <Route
        path="/commissions"
        element={
          <RequireRole>
            <Commissions />
          </RequireRole>
        }
      />
      <Route
        path="/bareme-commissions"
        element={
          <RequireRole>
            <CommissionPlan />
          </RequireRole>
        }
      />
      <Route
        path="/audit"
        element={
          <RequireRole>
            <AuditTrail />
          </RequireRole>
        }
      />
      <Route
        path="/system-settings"
        element={
          <RequireRole>
            <SystemSettings />
          </RequireRole>
        }
      />
      <Route
        path="/travauxbtp"
        element={
          <RequireRole>
            <TravauxBTPSupervision />
          </RequireRole>
        }
      />
      <Route
        path="/broadcast"
        element={
          <RequireRole>
            <BroadcastCenter />
          </RequireRole>
        }
      />
      <Route
        path="/cohorts"
        element={
          <RequireRole>
            <CohortsAnalysis />
          </RequireRole>
        }
      />
      <Route
        path="/health"
        element={
          <RequireRole>
            <TenantHealth />
          </RequireRole>
        }
      />
      <Route
        path="/feature-flags"
        element={
          <RequireRole>
            <FeatureFlags />
          </RequireRole>
        }
      />
      <Route
        path="/referrals"
        element={
          <RequireRole>
            <Referrals />
          </RequireRole>
        }
      />
      <Route
        path="/reviews"
        element={
          <RequireRole>
            <ReviewModeration />
          </RequireRole>
        }
      />
      <Route
        path="/access"
        element={
          <RequireRole>
            <AccessManagement />
          </RequireRole>
        }
      />
      <Route
        path="/categories"
        element={
          <RequireRole>
            <Categories />
          </RequireRole>
        }
      />
      <Route
        path="/api-keys"
        element={
          <RequireRole>
            <ApiKeys />
          </RequireRole>
        }
      />
      <Route
        path="/pricing"
        element={
          <RequireRole>
            <Pricing />
          </RequireRole>
        }
      />
      <Route
        path="/system-analytics"
        element={
          <RequireRole>
            <SystemAnalytics />
          </RequireRole>
        }
      />
      <Route
        path="/ai-costs"
        element={
          <RequireRole>
            <AiCosts />
          </RequireRole>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const App = () => (
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <UpdateBanner />
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </trpc.Provider>
);

export default App;
